<script lang="ts">
  import { browser } from '$app/environment';
  import NewsBannerCard from '$lib/components/News/NewsBannerCard.svelte';
  import NewsSmallBannerCard from '$lib/components/News/NewsSmallBannerCard.svelte';
  import MorphingWidgetLoader from '$lib/components/Shimmers/News/MorphingWidgetLoader.svelte';
  import { sendAnalyticsEvent } from '$lib/services/analytics';
  import { isBookmarkInIndexedDB } from '$lib/store/bookmarksStore';
  import { EventNames, EventSectionName } from '$lib/types/analytics';
  import type { NewsCDS } from '$lib/types/news';
  import { getHeroImage, getHeroVideoImage } from '$lib/utils/news';
  import Bookmark from '../Shared/Bookmark.svelte';
  import NewsBannerVideoCard from './NewsBannerVideoCard.svelte';
  import NewsSmallBannerVideoCard from './NewsSmallBannerVideoCard.svelte';

  export let promise: Promise<NewsCDS[] | undefined>;
  export let sectionName: EventSectionName = EventSectionName.CATEGORY_NEWS;

  const triggerAnalytics = (
    position: number,
    headline: string,
    publisher: string,
    category: string
  ) => {
    sendAnalyticsEvent(EventNames.CLICK_CARD, {
      section_name: sectionName,
      description: headline,
      publisher,
      category,
      card_position: position + 1
    });
  };
</script>

<div class="header-container">
  <slot name="header" />
</div>

{#await promise}
  <MorphingWidgetLoader />
{:then news}
  {#if news}
    {@const [article, ...remainingArticles] = news}
    <div class="container">
      {#if 'storyVideo' in article && article?.storyVideo !== null}
        <NewsBannerVideoCard
          heroImage={getHeroImage(article)}
          headline={article?.headline}
          publisherLogo={article?.outlet?.logo?.url}
          publisherName={article?.outlet?.outletName}
          category={article?.categories?.[0]}
          position={news.indexOf(article)}
          videoUrl={article?.storyVideo !== null ? article?.storyVideo[0].url : null}
          postId={article?.sys?.id}
          sourceUrl={article?.sourceUrl}
        >
          {#if browser}
            {#await isBookmarkInIndexedDB(article?.sys?.id) then isBookmarked}
              <Bookmark newsItem={article} postId={article?.sys?.id} {isBookmarked} />
            {/await}
          {/if}
        </NewsBannerVideoCard>
      {:else}
        <NewsBannerCard
          heroImage={getHeroImage(article)}
          postId={article?.sys?.id}
          headline={article?.headline}
          publisherLogo={article?.outlet?.logo?.url}
          publisherName={article?.outlet?.outletName}
          category={article?.categories?.[0]}
          ctaUrl={article?.sourceUrl}
          handleClick={() =>
            triggerAnalytics(
              0,
              article?.headline,
              article?.outlet?.outletName,
              article?.categories?.[0]
            )}
        >
          {#if browser}
            {#await isBookmarkInIndexedDB(article?.sys?.id) then isBookmarked}
              <Bookmark newsItem={article} postId={article?.sys?.id} {isBookmarked} />
            {/await}
          {/if}
        </NewsBannerCard>
      {/if}
      {#each remainingArticles as news, index (news?.sys?.id)}
        {#if 'storyVideo' in news && news?.storyVideo !== null}
          <div class="divider" />
          <NewsSmallBannerVideoCard
            heroImage={getHeroVideoImage(news)}
            headline={news?.headline}
            publisherLogo={news?.outlet?.logo?.url}
            publisherName={news?.outlet?.outletName}
            category={news?.categories?.[0]}
            position={index}
            videoUrl={news?.storyVideo !== null ? news?.storyVideo[0].url : null}
            postId={news?.sys?.id}
            sourceUrl={news?.sourceUrl}
          >
            {#if browser}
              {#await isBookmarkInIndexedDB(news?.sys?.id) then isBookmarked}
                <Bookmark newsItem={news} postId={news?.sys?.id} {isBookmarked} />
              {/await}
            {/if}
          </NewsSmallBannerVideoCard>
        {:else}
          <div class="divider" />
          <NewsSmallBannerCard
            heroImage={getHeroImage(news)}
            postId={news?.sys?.id}
            headline={news?.headline}
            category={news?.categories?.[0]}
            ctaUrl={news?.sourceUrl}
            handleClick={() =>
              triggerAnalytics(
                index,
                news?.headline,
                news?.outlet?.outletName,
                news?.categories?.[0]
              )}
          >
            {#if browser}
              {#await isBookmarkInIndexedDB(news?.sys?.id) then isBookmarked}
                <Bookmark newsItem={news} postId={news?.sys?.id} {isBookmarked} />
              {/await}
            {/if}
          </NewsSmallBannerCard>
        {/if}
      {/each}
    </div>
  {/if}
{/await}

<style>
  .container {
    padding: 12px;
  }

  .divider {
    width: 100%;
    height: 1px;
    background-color: var(--color-divider);
    opacity: 0.1;
    border-radius: 2px;
  }

  /* .header-container {
    padding: 12px;
  }  */
</style>
